// Public API for the player-overlays module: a generic, type-keyed manager for
// overlay windows layered over a single player stage. Wraps the lower-level
// `window.mountPlayerOverlayWindow` primitive (see ../overlay-window).

import type {
  OverlayWindowHandle,
  OverlayWindowOptions,
  OverlayWindowTarget,
} from '../overlay-window/types';
import type { PlayerOverlayManager } from './types';

/**
 * Resolve an OverlayWindowTarget (string id or HTMLElement) to an HTMLElement,
 * logging an error and returning null if a string id has no matching node.
 * Mirrors overlay-window/index.ts's resolveTarget.
 */
function resolveTarget(target: OverlayWindowTarget): HTMLElement | null {
  if (typeof target === 'string') {
    const el = document.getElementById(target);
    if (!el) {
      console.error('PlayerOverlayManager: target element not found:', target);
      return null;
    }
    return el;
  }
  return target;
}

// Per-target singleton registry. Keyed on the stage element so repeated calls
// for the same player return the same manager (per-type overlay state survives
// across callers), and managers for detached stages are garbage-collected.
const registry = new WeakMap<HTMLElement, PlayerOverlayManager>();

function createManager(target: HTMLElement): PlayerOverlayManager {
  // One live overlay window per type string.
  const handles = new Map<string, OverlayWindowHandle>();

  return {
    target,
    show(type: string, options: OverlayWindowOptions = {}): OverlayWindowHandle {
      // Replace any existing overlay of THIS type; leave other types alone.
      handles.get(type)?.unmount();
      handles.delete(type);

      const userOnClose = options.onClose;
      const handle = window.mountPlayerOverlayWindow(target, {
        ...options,
        // The window's own X button self-unmounts after firing this callback
        // (see overlay-window/index.ts). Drop our reference here so has()/hide()
        // stay accurate and we never double-unmount it.
        onClose: () => {
          userOnClose?.();
          handles.delete(type);
        },
      });
      handles.set(type, handle);
      return handle;
    },
    hide(type: string): void {
      const handle = handles.get(type);
      if (handle) {
        handle.unmount();
        handles.delete(type);
      }
    },
    has(type: string): boolean {
      return handles.has(type);
    },
    clear(): void {
      for (const handle of handles.values()) handle.unmount();
      handles.clear();
    },
  };
}

window.getPlayerOverlayManager = function getPlayerOverlayManager(
  targetOrId: OverlayWindowTarget,
): PlayerOverlayManager | null {
  const target = resolveTarget(targetOrId);
  if (!target) return null;
  let manager = registry.get(target);
  if (!manager) {
    manager = createManager(target);
    registry.set(target, manager);
  }
  return manager;
};

export type { PlayerOverlayManager } from './types';
